import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Box, Cylinder } from '@react-three/drei';
import * as THREE from 'three';
import { ShotData, ShotAnimation } from '../types';

interface BatProps {
  shot: ShotData;
  animation: ShotAnimation;
  showSweetSpot?: boolean;
}

export const Bat: React.FC<BatProps> = ({
  shot,
  animation,
  showSweetSpot = true
}) => {
  const batRef = useRef<THREE.Group>(null);
  const impactMarkerRef = useRef<THREE.Mesh>(null);

  // Calculate bat position and rotation based on animation
  const batTransform = useMemo(() => {
    const basePosition = new THREE.Vector3(-0.5, 1, -1); // Batsman's stance position
    const baseRotation = new THREE.Euler(0, Math.PI / 6, 0); // Slight angle towards off side
    
    if (!animation.isPlaying) {
      return { position: basePosition, rotation: baseRotation };
    }
    
    if (animation.currentPhase === 'preImpact') {
      // Bat swing animation
      const progress = animation.currentTime / animation.phases.preImpact.duration;
      const swingAngle = progress * Math.PI * 0.3; // 54 degree swing
      
      const rotation = new THREE.Euler(
        -swingAngle * 0.3, // Slight downward angle
        Math.PI / 6 + swingAngle * 0.5, // Rotation towards ball
        swingAngle * 0.2 // Slight roll
      );
      
      const position = basePosition.clone().add(
        new THREE.Vector3(
          progress * 0.3, // Move towards ball
          -progress * 0.2, // Slight drop
          progress * 0.8 // Forward movement
        )
      );
      
      return { position, rotation };
    }
    
    if (animation.currentPhase === 'impact') {
      // Bat at impact position
      const rotation = new THREE.Euler(-0.2, Math.PI / 4, 0.3);
      const position = new THREE.Vector3(-0.2, 1.3, -0.2);
      return { position, rotation };
    }
    
    if (animation.currentPhase === 'postImpact') {
      // Follow-through
      const progress = (animation.currentTime - animation.phases.postImpact.startTime) / 
                      animation.phases.postImpact.duration;
      
      const followThroughAngle = progress * Math.PI * 0.4;
      
      const rotation = new THREE.Euler(
        -0.2 - followThroughAngle * 0.2,
        Math.PI / 4 + followThroughAngle * 0.6,
        0.3 + followThroughAngle * 0.4
      );
      
      const position = new THREE.Vector3(
        -0.2 + progress * 0.4,
        1.3 + progress * 0.3,
        -0.2 + progress * 0.6
      );
      
      return { position, rotation };
    }
    
    return { position: basePosition, rotation: baseRotation };
  }, [animation]);

  // Update bat transform
  useFrame(() => {
    if (batRef.current) {
      batRef.current.position.copy(batTransform.position);
      batRef.current.rotation.copy(batTransform.rotation);
    }
  });

  // Bat materials
  const bladeMaterial = useMemo(() => 
    new THREE.MeshPhongMaterial({
      color: '#deb887',
      shininess: 50,
      specular: 0x111111
    }), []
  );

  const handleMaterial = useMemo(() =>
    new THREE.MeshPhongMaterial({
      color: '#8b4513',
      shininess: 30
    }), []
  );

  const sweetSpotMaterial = useMemo(() =>
    new THREE.MeshBasicMaterial({
      color: '#22c55e',
      transparent: true,
      opacity: 0.3
    }), []
  );

  const impactMaterial = useMemo(() =>
    new THREE.MeshBasicMaterial({
      color: '#ef4444',
      transparent: true,
      opacity: 0.8
    }), []
  );

  const rotationArray = batTransform.rotation.toArray();
  return (
    <group ref={batRef} position={batTransform.position} rotation={[(rotationArray[0] ?? 0) as number, (rotationArray[1] ?? 0) as number, (rotationArray[2] ?? 0) as number]}>
      {/* Bat handle */}
      <Cylinder
        args={[0.02, 0.025, 0.33]} // Handle: 33cm long, slight taper
        position={[0, 0.165, 0]}
        material={handleMaterial}
        castShadow
      />
      
      {/* Bat blade */}
      <Box
        args={[0.108, 0.6, 0.04]} // Blade: 10.8cm wide, 60cm long, 4cm thick
        position={[0, -0.135, 0]}
        material={bladeMaterial}
        castShadow
        receiveShadow
      />
      
      {/* Bat edges (additional detail) */}
      <Box
        args={[0.11, 0.02, 0.045]}
        position={[0, -0.42, 0]}
        material={bladeMaterial}
        castShadow
      />
      
      {/* Handle grip */}
      <Cylinder
        args={[0.026, 0.026, 0.25]}
        position={[0, 0.24, 0]}
        material={handleMaterial}
      />
      
      {/* Sweet spot indicator - positioned 10-15cm from bottom edge */}
      {showSweetSpot && (
        <mesh position={[0, -0.32, 0.021]}>
          <circleGeometry args={[0.03]} />
          <primitive object={sweetSpotMaterial} />
        </mesh>
      )}
      
      {/* Impact location marker */}
      {animation.currentPhase === 'impact' && (
        <mesh
          ref={impactMarkerRef}
          position={[shot.impactLocationX, shot.impactLocationY - 0.32, 0.021]}
        >
          <circleGeometry args={[0.015]} />
          <primitive object={impactMaterial} />
        </mesh>
      )}
      
      {/* Sweet spot miss indicator */}
      {showSweetSpot && shot.sweetSpotDistance > 0.02 && (
        <>
          {/* Line from sweet spot to impact */}
          <mesh position={[shot.impactLocationX / 2, (shot.impactLocationY - 0.32) / 2, 0.022]}>
            <boxGeometry args={[0.002, shot.sweetSpotDistance, 0.001]} />
            <meshBasicMaterial color="#fbbf24" />
          </mesh>
          
          {/* Distance text would go here in a real implementation */}
        </>
      )}
      
      {/* Bat speed indicator during swing */}
      {animation.isPlaying && animation.currentPhase === 'preImpact' && (
        <group>
          {/* Motion blur effect */}
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              args={[0.105, 0.58, 0.035]}
              position={[i * -0.05, -0.135, i * -0.02]}
              material={new THREE.MeshBasicMaterial({
                color: '#deb887',
                transparent: true,
                opacity: 0.1 * (5 - i)
              })}
            />
          ))}
        </group>
      )}
      
      {/* Impact effect */}
      {animation.currentPhase === 'impact' && (
        <group position={[shot.impactLocationX, shot.impactLocationY - 0.32, 0.025]}>
          {/* Impact flash */}
          <mesh>
            <sphereGeometry args={[0.05]} />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={0.9}
            />
          </mesh>
          
          {/* Force lines */}
          {[...Array(6)].map((_, i) => (
            <mesh
              key={i}
              position={[
                Math.cos(i * Math.PI / 3) * 0.03,
                Math.sin(i * Math.PI / 3) * 0.03,
                0
              ]}
              rotation={[0, 0, i * Math.PI / 3]}
            >
              <boxGeometry args={[0.001, 0.02, 0.001]} />
              <meshBasicMaterial color="#fbbf24" />
            </mesh>
          ))}
        </group>
      )}
      
      {/* Power transfer visualization */}
      {animation.currentPhase === 'impact' && (
        <mesh position={[0, -0.32, 0.03]}>
          <cylinderGeometry args={[0.06 * shot.powerTransfer, 0.06 * shot.powerTransfer, 0.002]} />
          <meshBasicMaterial
            color={shot.powerTransfer > 0.8 ? "#22c55e" : shot.powerTransfer > 0.6 ? "#fbbf24" : "#ef4444"}
            transparent
            opacity={0.7}
          />
        </mesh>
      )}
    </group>
  );
};